import MojitoCore from 'mojito/core';
import { omitBy, set, get } from 'mojito/utils';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import Overlay from 'presentation/components/overlay/index.js';
import FlexPane from 'presentation/components/flex-pane/index.js';
import AbsolutePane from 'presentation/components/absolute-pane/index.js';
import { STATE } from './index';

const { merge } = MojitoCore.Base.objUtils;
const { useResizeDetector } = MojitoCore.Base.resizeDetector;
const { cssTimeToMs } = MojitoCore.Base.NumberUtils;

const BottomSheet = props => {
    const {
        state,
        children,
        headerElement,
        mojitoTools: { config, style },
    } = props;

    const { overlay } = config;

    const isDraggingRef = useRef(false);
    const wasDraggingRevertedRef = useRef(false);
    const swipeRef = useRef(false);
    const [transform, setTransform] = useState(undefined);
    const [isRevertingDrag, setIsRevertingDrag] = useState(false);
    const [renderedState, setRenderedState] = useState(state);
    const defaultOverlayOpacity = get(overlay, 'style.overlay.opacity');
    const [overlayOpacity, setOverlayOpacity] = useState(defaultOverlayOpacity);

    const pointerDownRef = useRef(false);
    const startDragRef = useDragRef();
    const endDragRef = useDragRef();

    const { elementRef: overlayRef, height: overlayHeight } = useResizeDetector({
        useDebounce: false,
    });
    const { elementRef: containerRef, element: containerElement } = useResizeDetector({
        useDebounce: false,
    });
    const commitedState = useCommitedState(state, containerElement, config.animationDuration);
    if (commitedState === state) {
        swipeRef.current = false;
    }

    const onDragRevertEnd = useCallback(
        event => {
            if (event.target === containerElement) {
                containerElement.removeEventListener('transitionend', onDragRevertEnd);
                setIsRevertingDrag(false);
            }
        },
        [containerElement]
    );

    useEffect(() => setOverlayOpacity(defaultOverlayOpacity), [defaultOverlayOpacity]);
    useEffect(() => setRenderedState(state), [state]);
    useEffect(() => {
        return () => {
            containerElement?.removeEventListener('transitionend', onDragRevertEnd);
        };
    }, [containerElement, onDragRevertEnd]);

    const onPointerDown = e => {
        e.stopPropagation();
        e.preventDefault();

        pointerDownRef.current = true;

        startDragRef.current = {
            time: Date.now(),
            distance: e.clientY,
            point: e.clientY,
            top: containerElement.getBoundingClientRect().top,
        };
    };

    const onPointerMove = e => {
        e.stopPropagation();
        e.preventDefault();

        if (!pointerDownRef.current) {
            return;
        }

        // calculate new position of the sheet. This is dragging we don't need any transition for this case.
        const currentPoint = startDragRef.current.point;
        const deltaY = e.clientY - currentPoint;
        const newTop = startDragRef.current.top + deltaY;
        const deltaTop = newTop - startDragRef.current.top;

        if (Math.abs(deltaTop) > 1 && newTop >= 0) {
            isDraggingRef.current = true;
            setTransform({
                transform: `translateY(${newTop}px)`,
                transition: `none`,
                height: '100%',
            });
        }

        startDragRef.current.draggingDown = deltaY > 0;

        // calculate opacity
        if (defaultOverlayOpacity > 0 && Math.abs(1 - deltaY) > 1) {
            const draggingUp = deltaY < 0;
            const currentTop = startDragRef.current.top;
            const totalPathToDrag = draggingUp ? currentTop : overlayHeight - currentTop;
            const pathDragged = Math.min(Math.abs(currentTop - newTop), totalPathToDrag);
            const percentDragged = (pathDragged * 100) / totalPathToDrag;

            const aimingOpacity = draggingUp ? defaultOverlayOpacity : 1;
            const opacity = (aimingOpacity * percentDragged) / 100;
            const adaptedOpacity = draggingUp ? opacity : 1 - opacity;
            !isNaN(adaptedOpacity) && setOverlayOpacity(adaptedOpacity);
        }
    };

    const onPointerUp = e => {
        e.stopPropagation();
        e.preventDefault();

        pointerDownRef.current = false;
        if (!isDraggingRef.current) {
            return;
        }

        endDragRef.current = {
            time: Date.now(),
            distance: e.clientY,
        };
        isDraggingRef.current = false;

        const startY = startDragRef.current.distance;
        const endY = endDragRef.current.distance;
        const dragDistance = endY - startY;

        const shouldSwipe = Math.abs(dragDistance) > config.dragSensibility;
        if (shouldSwipe && dragDistance > 0 && props.onSwipeDown) {
            // quick swipe down
            swipeRef.current = true;
            props.onSwipeDown();
        } else if (shouldSwipe && dragDistance < 0 && props.onSwipeUp) {
            // quick swipe up
            swipeRef.current = true;
            props.onSwipeUp();
        } else {
            containerElement?.addEventListener('transitionend', onDragRevertEnd);
            // everything else
            setTransform({
                transform: `translateY(${startDragRef.current.top}px)`,
                height: '100%',
            });
            setIsRevertingDrag(true);
        }

        setOverlayOpacity(defaultOverlayOpacity);
    };

    const isDragging = isDraggingRef.current;
    const inMotion = isDragging || isRevertingDrag;
    const isDraggingDown = startDragRef.current.draggingDown;
    const draggingTransform = inMotion ? transform : undefined;
    const draggingWasReverted = wasDraggingRevertedRef.current;
    const slidingOptions = {
        element: containerElement,
        style,
        state,
        renderedState,
        commitedState,
        isSwiped: swipeRef.current,
        totalHeight: overlayHeight,
        transform: draggingTransform,
        disableTransition: draggingWasReverted,
    };
    const containerStyle = useSlidingContainerStyle(slidingOptions);
    wasDraggingRevertedRef.current = isRevertingDrag;

    const overlayConfig = useMemo(() => {
        const isClosing = state === STATE.PARTLY_OPENED && isDraggingDown;
        const opacity = state === STATE.OPENED || (isDragging && !isClosing) ? overlayOpacity : 0;
        const overlayOverride = set({}, 'style.overlay.opacity', opacity);
        overlayOverride.disableBodyScrolling = state === STATE.OPENED;
        return merge(config.overlay, overlayOverride);
    }, [overlayOpacity, state, config.overlay, isDragging, isDraggingDown]);

    return (
        <Overlay visible={true} config={overlayConfig} elementRef={overlayRef}>
            <div style={containerStyle} className="ta-BottomSheet" ref={containerRef}>
                <AbsolutePane
                    onPointerUp={onPointerUp}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    class={'ta-swiperIndicatorContainer'}
                    config={config.swiperIndicatorContainer}
                >
                    <FlexPane config={config.swiperIndicator} class={'ta-swiperIndicator'} />
                </AbsolutePane>
                {headerElement}
                <AbsolutePane config={config.shimPlaceholder} />
                <FlexPane config={config.contentContainer}>{children}</FlexPane>
            </div>
        </Overlay>
    );
};

BottomSheet.getStyle = (config, mode, merge) => {
    const { offset, ...restStyles } = config.style;
    const containerBase = merge(
        {
            position: 'fixed',
            width: '100%',
            transition: `transform ${config.animationDuration} ${config.animationFunction}`,
            willChange: 'transform',
            boxSizing: 'border-box',
            ...omitBy(restStyles, val => val === undefined),
        },
        offset
    );
    const { OPENED, PARTLY_OPENED, CLOSED } = STATE;
    return {
        containerBase,
        [OPENED]: merge(containerBase, { transform: 'translateY(0px)', height: '100%' }),
        [PARTLY_OPENED]: merge(containerBase, { bottom: 0, transform: 'translateY(0px)' }),
        [CLOSED]: merge(containerBase, {
            bottom: 0,
            transform: 'translateY(100%)',
        }),
    };
};

const useDragRef = () => useRef({ time: 0, distance: 0 });

const useSlidingContainerStyle = ({
    element,
    style,
    state,
    renderedState,
    commitedState,
    isSwiped,
    totalHeight,
    transform,
    disableTransition,
}) => {
    return useMemo(() => {
        if (transform) {
            return merge(style.containerBase, transform);
        }
        // We only put height: '100%' to the content in case it is getting closed from fully opened state. No need to do this for partly opened as the content will jump up.
        if (state === STATE.CLOSED && commitedState === STATE.OPENED) {
            const containerStyle = style[state];
            return merge(containerStyle, { height: '100%' });
        }
        // There is intention to fully open bottom sheet but the state was not rendered yet.
        if (state === STATE.OPENED && renderedState !== STATE.OPENED && !isSwiped) {
            const elementHeight = element.getBoundingClientRect().height;
            const offset = totalHeight - elementHeight;
            // This is need to stabilize partly opened state to ensure smooth transition to the OPENED state.
            // Here we are avoiding bottom property and using transform instead. That allows full betslip to render without jumping up,
            // afterward on useEffect the renderedState will be set to OPENED which will apply style with transition that will smoothly animate sheet to the final position on the top.
            return merge(style.containerBase, {
                transform: `translateY(${offset}px)`,
                transition: 'none',
                height: '100%',
            });
        }
        const containerStyle = style[state];
        return disableTransition ? merge(containerStyle, { transition: 'none' }) : containerStyle;
    }, [
        state,
        style,
        transform,
        element,
        totalHeight,
        renderedState,
        disableTransition,
        isSwiped,
        commitedState,
    ]);
};

const useCommitedState = (state, containerElement, transitionDuration) => {
    const duration = cssTimeToMs(transitionDuration);
    const [commitedState, setCommitedState] = useState();

    const onTransitionEnd = useCallback(
        event => {
            if (event.target === containerElement) {
                setCommitedState(state);
            }
        },
        [containerElement, state]
    );
    useEffect(() => {
        containerElement?.addEventListener('transitionend', onTransitionEnd);
        return () => {
            containerElement?.removeEventListener('transitionend', onTransitionEnd);
        };
    }, [containerElement, onTransitionEnd]);

    return duration > 0 ? commitedState : state;
};

export default BottomSheet;
